import React from "react";
import { motion } from "framer-motion";
import { Lock, Play, Check } from "lucide-react";
import { API } from "../lib/api";
import { IconByName, coverBg, coverInk } from "../lib/wordIcon";

// A large rounded tile for a single poem on the landing/dashboard grid.
export default function PoemTile({ poem, onClick, lockedLabel = "Sign in to unlock" }) {
    // Trust the backend's `locked` fully — it already accounts for
    // is_free_preview correctly (unlocked for anonymous/base access, but
    // still subject to a specific learner's own weekly schedule once a
    // learner_id is in play). Re-checking is_free_preview here would silently
    // override a real per-learner lock and show a poem as open when it isn't.
    const locked = poem.locked;
    const comingSoon = poem.is_coming_soon;
    const bg = locked ? "bg-lockedBg" : coverBg[poem.cover_color] || "bg-sand";
    const ink = locked ? "text-lockedText" : coverInk[poem.cover_color] || "text-charcoal";
    const showIcon = poem.show_cover_icon !== false;
    const coverIconSrc = poem.cover_icon_image_id ? `${API}/poems/${poem.poem_id}/cover-icon` : null;

    return (
        <motion.button
            data-testid={`poem-tile-${poem.order_index}`}
            onClick={onClick}
            whileHover={{ y: -4, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="group relative w-full text-left rounded-[2rem] shadow-lg shadow-black/5 overflow-hidden focus:outline-none focus:ring-4 focus:ring-honey/60 bg-white flex flex-col"
        >
            {/* Completed = all 5 real lessons done. Purely informational —
                a completed poem stays exactly as open/reachable as before,
                this never re-locks or hides anything. */}
            {!locked && poem.week_completed && (
                <span
                    className="absolute top-3 right-3 z-20 rounded-full bg-brightGreen text-white p-1 shadow-sm"
                    title="Completed"
                    data-testid={`poem-tile-completed-${poem.order_index}`}
                >
                    <Check className="w-3.5 h-3.5" strokeWidth={3.2} />
                </span>
            )}

            {/* Top zone: the poem's pastel color framing a big hardcover-style
                cover image — matches the reference's book-cover-container,
                while keeping each poem's own color for variety. */}
            <div className={`relative z-10 ${bg} px-3 pt-5 pb-3 grain flex justify-center`}>
                {showIcon && coverIconSrc ? (
                    <div className="w-full max-w-[300px]">
                        <img
                            src={coverIconSrc}
                            alt=""
                            className="w-full aspect-[0.8] object-cover rounded-[3px_12px_12px_3px] ring-1 ring-black/5 shadow-[-6px_10px_22px_rgba(10,25,45,0.3),0_3px_8px_rgba(0,0,0,0.15)] border-l-[3px] border-black/20 transition-transform duration-300 ease-out group-hover:-translate-y-0.5 group-hover:rotate-[-2deg] group-hover:scale-[1.03]"
                        />
                    </div>
                ) : showIcon ? (
                    <div className={`${ink} rounded-2xl bg-white/60 backdrop-blur p-3 shadow-sm overflow-hidden inline-flex`}>
                        <IconByName name={poem.cover_icon} src={coverIconSrc} className="w-10 h-10" strokeWidth={2.4} />
                    </div>
                ) : null}
            </div>

            {/* Bottom zone: plain white info panel — small gold "POEM X OF 20"
                tag + navy title, same as the reference cards. */}
            <div className="relative z-10 bg-white px-5 pt-4 pb-5 flex-1 flex flex-col justify-between">
                <p className="font-display text-sm font-bold tracking-[0.02em] text-black">
                    Poem {poem.order_index} of 20
                </p>
                <div className={`mt-3 flex items-center gap-2 text-sm font-semibold ${locked ? "text-lockedText" : "text-charcoal"}`}>
                    {locked ? (
                        <>
                            <Lock className="w-4 h-4" strokeWidth={2.5} />
                            <span>{lockedLabel}</span>
                        </>
                    ) : comingSoon ? (
                        <span className="rounded-full bg-honeySoft px-3 py-1">Coming soon</span>
                    ) : (
                        <>
                            <Play className="w-4 h-4 fill-current" strokeWidth={2.5} />
                            <span>Tap to play</span>
                        </>
                    )}
                </div>
            </div>
        </motion.button>
    );
}
